/**
 * Utility functions for managing smartChips in form schema
 * Handles collection, cleaning, and transformation of workflow variables
 * @author Sarthak Arora
 */

/**
 * Collects all workflow variable IDs from form data (including nested gridElements)
 * @param {Object} formData - The form data object
 * @returns {Set<string>} Set of unique workflow variable IDs
 */
export const collectWorkflowVariableIds = (formData) => {
  const workflowVariableIds = new Set();

  const traverseElement = (element) => {
    if (!element) return;

    // Check if element is a workflow variable
    // Can be identified by either:
    // 1. Old structure: properties.isWorkflowVariable && properties.workflowVariableId
    // 2. New structure: smartChipId at root level
    if (element.properties?.isWorkflowVariable && element.properties?.workflowVariableId) {
      workflowVariableIds.add(element.properties.workflowVariableId);
    } else if (element.smartChipId) {
      // New structure: use smartChipId directly
      workflowVariableIds.add(element.smartChipId);
    }

    // Traverse nested grid elements
    if (element.type === "row-container" && element.gridElements) {
      element.gridElements.forEach((row) => {
        row.forEach((gridElement) => {
          traverseElement(gridElement);
        });
      });
    }
  };

  // Traverse all subsections and elements
  formData?.subsections?.forEach((subsection) => {
    subsection.elements?.forEach((element) => {
      traverseElement(element);
    });
  });

  formData?.hiddenSubsections?.forEach((subsection) => {
    subsection.elements?.forEach((element) => {
      traverseElement(element);
    });
  });

  return workflowVariableIds;
};

/**
 * Gets workflow variable objects from customWorkflowVariables by IDs
 * @param {Array} customWorkflowVariables - Array of all workflow variables
 * @param {Set<string>} workflowVariableIds - Set of workflow variable IDs to fetch
 * @returns {Array} Array of workflow variable objects
 */
export const getWorkflowVariablesByIds = (customWorkflowVariables, workflowVariableIds) => {
  if (!customWorkflowVariables || !workflowVariableIds) return [];

  return Array.from(workflowVariableIds)
    .map((id) => customWorkflowVariables.find((variable) => variable.id === id))
    .filter((variable) => variable !== undefined);
};

/**
 * Builds smartChips object from workflow variables (keyed by ID for O(1) lookup)
 * @param {Array} workflowVariables - Array of workflow variable objects
 * @returns {Object} Object with workflow variable IDs as keys and smartChip objects as values
 */
export const buildSmartChipsObject = (workflowVariables) => {
  if (!workflowVariables) return {};

  const smartChips = {};
  workflowVariables.forEach((variable) => {
    // Return the full workflow variable object as-is
    // This includes: id, name, type, attributes, properties, etc.
    smartChips[variable.id] = {
      ...variable,
      isDeleted: false, // Flag to track if variable was deleted from workflow
    };
  });

  return smartChips;
};

/**
 * Cleans element properties by removing workflow variable-specific fields
 * Keeps only form-specific properties like label, isHidden, etc.
 * @param {Object} element - The element to clean
 * @param {Object} workflowVariable - The workflow variable object (to identify which properties to remove)
 * @returns {Object} Cleaned properties object
 */
export const cleanElementProperties = (element, workflowVariable) => {
  if (!element?.properties) return element.properties || {};

  const properties = { ...element.properties };

  // If we have the workflow variable, remove all properties that exist in its attributes
  // smartChips has precedence, so we remove all workflow variable attributes from element properties
  if (workflowVariable?.attributes) {
    Object.keys(workflowVariable.attributes).forEach((key) => {
      if (properties.hasOwnProperty(key)) {
        delete properties[key];
      }
    });
  }

  return properties;
};

/**
 * Adds smartChipId to element and cleans its properties
 * @param {Object} element - The element to transform
 * @param {Map} workflowVariableMap - Map of workflow variable ID to workflow variable object
 * @returns {Object} Transformed element with smartChipId and cleaned properties
 */
export const transformElementWithSmartChip = (element, workflowVariableMap) => {
  if (!element) return element;

  const transformedElement = { ...element };

  // Add smartChipId at root level
  if (element.properties?.isWorkflowVariable && element.properties?.workflowVariableId) {
    transformedElement.smartChipId = element.properties.workflowVariableId;
  }

  // Get workflow variable for this element
  const workflowVariableId = element.properties?.workflowVariableId;
  const workflowVariable = workflowVariableId ? workflowVariableMap?.get(workflowVariableId) : null;

  // Clean properties
  transformedElement.properties = cleanElementProperties(element, workflowVariable);

  // Transform nested grid elements
  if (element.type === "row-container" && element.gridElements) {
    transformedElement.gridElements = element.gridElements.map((row) =>
      row.map((gridElement) => {
        if (!gridElement) return null;
        return transformElementWithSmartChip(gridElement, workflowVariableMap);
      })
    );
  }

  return transformedElement;
};

/**
 * Transforms form data to use smartChips structure
 * @param {Object} formData - The form data to transform
 * @param {Array} customWorkflowVariables - Array of all workflow variables
 * @returns {Object} Object containing transformed formData and smartChips object
 */
export const transformFormDataWithSmartChips = (formData, customWorkflowVariables) => {
  if (!formData || !customWorkflowVariables) {
    return {
      transformedFormData: formData,
      smartChips: {},
    };
  }

  // Extract the actual form content
  const actualFormData = formData.subsections ? formData : formData.content || formData;

  // Step 1: Collect all workflow variable IDs
  const workflowVariableIds = collectWorkflowVariableIds(actualFormData);

  // Step 2: Get workflow variable objects
  const workflowVariables = getWorkflowVariablesByIds(customWorkflowVariables, workflowVariableIds);

  // Step 3: Build smartChips object (keyed by ID)
  const smartChips = buildSmartChipsObject(workflowVariables);

  // Step 4: Create a map for quick lookup during transformation
  const workflowVariableMap = new Map();
  workflowVariables.forEach((variable) => {
    workflowVariableMap.set(variable.id, variable);
  });

  // Step 5: Transform form data
  const transformedFormData = {
    ...actualFormData,
    subsections: actualFormData.subsections?.map((subsection) => ({
      ...subsection,
      elements: subsection?.elements?.map((element) => transformElementWithSmartChip(element, workflowVariableMap)),
    })),
    hiddenSubsections: actualFormData?.hiddenSubsections?.map((subsection) => ({
      ...subsection,
      elements: subsection?.elements?.map((element) => transformElementWithSmartChip(element, workflowVariableMap)),
    })),
  };

  return {
    transformedFormData,
    smartChips,
  };
};

/**
 * Merges smartChip data into element properties for rendering
 * smartChips has precedence, but form-specific properties are preserved
 * @param {Object} element - The element to merge
 * @param {Object} smartChips - Object with smartChip IDs as keys
 * @returns {Object} Element with merged properties
 */
export const mergeSmartChipIntoElement = (element, smartChips) => {
  try {
    if (!element) return element;

    // If element is a row-container, process it to recurse into gridElements
    // even if it doesn't have a smartChipId itself
    if (element.type === "row-container" && element.gridElements) {
      return {
        ...element,
        gridElements: element.gridElements.map((row) =>
          row.map((gridElement) => {
            if (!gridElement) return null;
            return mergeSmartChipIntoElement(gridElement, smartChips);
          })
        ),
      };
    }

    // If element doesn't have smartChipId, return as-is
    if (!element.smartChipId) {
      return element;
    }

    // Get the smartChip data
    const smartChip = smartChips[element.smartChipId];

    if (!smartChip) {
      // Mark as deleted if workflow variable smartChip is missing
      // This ensures deleted workflow variables don't appear in FormFiller
      return {
        ...element,
        isDeleted: true,
        properties: {
          ...element.properties,
          isWorkflowVariable: true,
          workflowVariableId: element.smartChipId
        }
      };
    }

    const { adminType = null, adminCustomVariables = null } = smartChip;
    const hasDropdownOptions =
      Array.isArray(smartChip.attributes?.options) &&
      smartChip.attributes.options.some((opt) => opt && String(opt.label ?? "").trim() !== "");
    const isActiveMember = adminType === "admin" && adminCustomVariables === null && !hasDropdownOptions 

    // Merge smartChip attributes into element properties
    // smartChips has precedence, but form-specific properties are preserved
    const mergedProperties = {
      ...element.properties, // Form-specific properties first (label, isHidden, etc.)
      ...smartChip.attributes, // Then smartChip attributes (overrides form-specific if same key)
      // Add workflow variable identifiers for backward compatibility
      workflowVariableId: smartChip.id,
      isWorkflowVariable: true,
      isActiveMember
    };
    mergedProperties.showChipName = mergedProperties.showChipName === false ? false : true;

    const mergedElement = {
      ...element,
      properties: mergedProperties,
      isDeleted: smartChip?.isDeleted,
    };

    // Handle nested grid elements (for elements that have smartChipId AND are row-containers)
    // Note: row-containers without smartChipId are already handled above
    if (element.type === "row-container" && element.gridElements) {
      mergedElement.gridElements = element.gridElements.map((row) =>
        row.map((gridElement) => {
          if (!gridElement) return null;
          return mergeSmartChipIntoElement(gridElement, smartChips);
        })
      );
    }
    return mergedElement;
  } catch (error) {
    console.error("Error merging smartChip into element:", error);
    return element;
  }
};

/**
 * Merges smartChips into form data for rendering
 * Transforms elements with smartChipId to have merged properties
 * @param {Object} formData - The form data (may have smartChips array or object)
 * @param {Object} smartChips - Object with smartChip IDs as keys (from form schema)
 * @returns {Object} Form data with merged properties
 */
export const mergeSmartChipsIntoFormData = (formData, smartChips) => {
  if (!formData || !smartChips || Object.keys(smartChips).length === 0) {
    return formData;
  }

  return {
    ...formData,
    subsections: formData.subsections?.map((subsection) => ({
      ...subsection,
      elements: subsection.elements?.map((element) => mergeSmartChipIntoElement(element, smartChips)),
    })),
    hiddenSubsections: formData.hiddenSubsections?.map((subsection) => ({
      ...subsection,
      elements: subsection.elements?.map((element) => mergeSmartChipIntoElement(element, smartChips)),
    })),
  };
};
